<template>
  <div>
    <div class="float-right">
      <FormInputText placeholder="Search" v-model="searchText" icon="fa-search" class="d-inline-block mr-2" />
      <FormButton class="btn-warning btn-xs mr-2" @click="clearAllocations"><i class="far fa-trash-alt fa-fw"></i> Clear Allocations</FormButton>
      <FormButton class="btn-primary btn-xs" @click="autoAllocation"><i class="far fa-magic fa-fw"></i> Auto Allocate</FormButton>
    </div>
    <ContentTitle>Invoice Allocations</ContentTitle>
    <ContentSubTitle>{{availableInvoiceList.length}} invoice{{availableInvoiceList.length | displayCountPlural}} available for {{paymentsTitle}}</ContentSubTitle>

    <div class="invoice-list-scroll">
      <StaticDataTable class="static-table-list">
        <template v-slot:header>
          <tr>
            <th width="10%">Invoice</th>
            <th width="10%">Order</th>
            <th width="10%">Reference</th>
            <th width="10%">Due Date</th>
            <th width="10%">Total</th>
            <th width="10%">Amount Due</th>
            <th width="25%">Allocation</th>
            <th width="10%">Remaining</th>
            <th width="5%">Actions</th>
          </tr>
        </template>
        <template v-slot:body>
          <tr v-for="invoice in filteredInvoiceList" :key="invoice.id" :class="{'row-hightlight': invoice.id == defaultInvoiceId}">
            <td class="font-weight-bold">
              <LinkInvoice :id="invoice.id" :name="`#${invoice.sequenceNumber}`" />
            </td>
            <td>
              <LinkOrder :id="invoice.orderId" :name="`#${invoice.order.sequenceNumber}`" v-if="invoice.order" />
            </td>
            <td>
              {{invoice.customerRef | formatHyphenEmptyValue}}
            </td>
            <td :class="{'text-danger': invoice.isOverDue}">
              {{invoice.dateDue | formatDate}}
            </td>            
            <td>
              {{invoice.totalPrice | formatCurrency}}
            </td>
            <td>
              {{invoice.amountDue | formatCurrency}}
            </td>
            <td>
              <FormInputCurrency v-model="invoice.allocatedAmount" @input="invoiceListUpdated()" />
            </td>
            <td>
              <span :class="{'result-fg-success': isInvoicePaidOff(invoice), 'result-fg-failed': isInvoiceInvalidAmount(invoice)}">{{invoice.amountDue - invoice.allocatedAmount | formatCurrency}}</span>
              <Icon icon="fa-check-circle" iconStyle="fas" iconClass="result-fg-success" v-if="isInvoicePaidOff(invoice)" class="ml-1" />
              <Icon icon="fa-exclamation-circle" iconStyle="fas" iconClass="result-fg-failed" v-if="isInvoiceInvalidAmount(invoice)" class="ml-1" />
            </td>
            <td>
              <ActionDropMenu :tableAction="true">
                <DropdownMenuItem v-if="!isFixedAmount" @click="addInvoiceToPayment(invoice)" icon="fa-usd-circle">Increase Payment</DropdownMenuItem>
                <DropdownMenuDivider v-if="!isFixedAmount" />
                <DropdownMenuItem @click="addInvoiceAllocation(invoice)" icon="fa-plus-circle">Allocate</DropdownMenuItem>
                <DropdownMenuItem @click="clearInvoiceAllocation(invoice)" icon="fa-minus-circle">Clear</DropdownMenuItem>
              </ActionDropMenu>
            </td>
          </tr>
        </template>
        <template v-slot:empty v-if="availableInvoiceList.length == 0">No unpaid invoices found</template>
      </StaticDataTable>
    </div>
    <StaticDataTable class="static-table-list">
      <template v-slot:header>
        <tr class="border-top">
          <th width="10%">Totals</th>
          <th width="20%" colspan="2">{{availableInvoiceList.length}} invoice{{availableInvoiceList.length | displayCountPlural}}</th>
          <th width="10%">{{totalInvoiceOverdueCount}} Overdue</th>
          <th width="10%">{{totalInvoicePrice | formatCurrency}}</th>
          <th width="10%">{{totalInvoiceAmountDue | formatCurrency}}</th>
          <th width="25%">{{totalInvoiceAllocation | formatCurrency}}</th>
          <th width="10%">{{totalInvoiceRemaining | formatCurrency}}</th>
          <th width="5%"> </th>
        </tr>
      </template>
    </StaticDataTable>
  </div>
</template>

<style scoped>
.invoice-list-scroll {
  height: 250px;
  overflow-y: scroll;
}
</style>

<script>
import _ from 'lodash'
import moment from 'moment'
import Utils from '@/utils/utils'

import { InvoiceStatusTypes } from '@/store/types'

export default {
  name: 'InvoiceAllocation',
  model: {
    event: 'update'
  },
  props: {
    contactId: Number,
    defaultInvoiceId: Number,
    isFixedAmount: Boolean,
    isPaymentMode: Boolean,
    isAllocationOnly: Boolean,
    paymentAmount: Number,
    value: Array,
  },
  data() {
    return {
      availableInvoiceList: [],
      loading: false,
      loadingError: '',
      searchText: ''
    }
  },
  watch: {
    contactId: {
      immediate: true,
      handler: function() {
        this.requestInvoiceList()
      }
    },
    value: function() {
      if(this.value) {
        this.availableInvoiceList = _.cloneDeep(this.value)
      }
    }
  },
  computed: {
    currentPayment() {
      return this.parseDecimal(this.paymentAmount)
    },
    paymentsTitle() {
      return this.isPaymentMode ? 'payments' : 'crediting'
    },
    totalInvoicePrice() {
      return this.availableInvoiceList.reduce((curVal, invoice) => {
        return Utils.roundCurrency(curVal + invoice.totalPrice)
      }, 0)
    },
    totalInvoiceAmountDue() {
      return this.availableInvoiceList.reduce((curVal, invoice) => {
        return Utils.roundCurrency(curVal + invoice.amountDue)
      }, 0)
    },
    totalInvoiceAllocation() {
      return this.availableInvoiceList.reduce((curVal, invoice) => {
        return Utils.roundCurrency(curVal + this.parseDecimal(invoice.allocatedAmount))
      }, 0)
    },
    totalInvoiceRemaining() {
      return this.availableInvoiceList.reduce((curVal, invoice) => {
        return Utils.roundCurrency(curVal + (invoice.amountDue - this.parseDecimal(invoice.allocatedAmount)))
      }, 0)
    },
    totalOverpaymentValue() {
      let overAmount = this.currentPayment - this.totalInvoiceAllocation
      return Math.max(overAmount, 0)
    },
    totalInvoiceOverdueCount() {
      return this.availableInvoiceList.filter(invoice => invoice.isOverDue).length
    },
    totalInvoicePartialPaidCount() {
      return this.availableInvoiceList.filter(invoice => this.parseDecimal(invoice.allocatedAmount) > 0 && this.parseDecimal(invoice.allocatedAmount) < invoice.amountDue).length
    },
    totalInvoiceFullPaidCount() {
      return this.availableInvoiceList.filter(invoice => Utils.roundCurrency(this.parseDecimal(invoice.allocatedAmount) - invoice.amountDue) == 0).length
    },
    totalInvoiceErrorCount() {
      return this.availableInvoiceList.filter(invoice => this.parseDecimal(invoice.allocatedAmount) > invoice.amountDue).length
    },
    filteredInvoiceList() {
      if(!this.searchText)
        return this.availableInvoiceList

      let searchText = (this.searchText+'').trim().toLowerCase()

      return this.availableInvoiceList.filter(invoice => {
        if(invoice.sequenceNumber && invoice.sequenceNumber.toLowerCase().includes(searchText))
          return true
        if(invoice.customerRef && invoice.customerRef.toLowerCase().includes(searchText))
          return true
        if((invoice.totalPrice+'').includes(searchText))
          return true
        if((invoice.amountDue+'').includes(searchText))
          return true
        return false
      })
    }
  },
  methods: {
    requestInvoiceList() {
      if(this.contactId == null) {
        this.availableInvoiceList = []
        this.invoiceListUpdated()
        return
      }
      // Get the list of invoices for this contact available for payment
      this.loading = true
      this.loadingError = ''
      this.availableInvoiceList = []

      // Unpaid invoices for this contact, due oldest first
      var options = {
        filter: { contactId: this.contactId, status: InvoiceStatusTypes.invoiced },
        order: [{ column: 0, dir: 'ASC' }, { column: 1, dir: 'ASC' }],
        columns: [{ data: 'dateDue' }, { data: 'createdAt' }],
      }      

      this.$apiService.accountsService.getInvoiceFilteredList(options)
      .then(response => {

        this.availableInvoiceList = response.data.map(invoice => {
          return {
            id: invoice.id,
            sequenceNumber: invoice.sequenceNumber,
            dateInvoiceCreated: invoice.dateInvoiceCreated,
            dateDue: invoice.dateDue,
            isOverDue: moment(invoice.dateDue).local().endOf('day').isBefore(moment()),
            customerRef: invoice.customerRef,
            orderId: invoice.orderId,
            order: invoice.order,
            totalPrice: invoice.totalPrice,
            amountDue: invoice.amountDue,
            allocatedAmount: 0,
          }
        })
        
        // If invoiceId set, show it at the top of the list
        // Auto pay the selected invoice
        if(this.defaultInvoiceId) {
          let selectedInvoice = this.availableInvoiceList.find(invoice => invoice.id == this.defaultInvoiceId)
          if(selectedInvoice) {
            this.availableInvoiceList = _.concat(selectedInvoice, this.availableInvoiceList.filter(invoice => invoice.id != this.defaultInvoiceId))
            
            this.availableInvoiceList[0].allocatedAmount = selectedInvoice.amountDue
            this.$emit('updatePayment', selectedInvoice.amountDue)
          }
        }
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
        this.invoiceListUpdated()
      })
    },
    isInvoicePaidOff(invoice) {
      return Utils.roundCurrency(invoice.amountDue - this.parseDecimal(invoice.allocatedAmount)) == 0
    },
    isInvoiceInvalidAmount(invoice) {
      return Utils.roundCurrency(invoice.amountDue - this.parseDecimal(invoice.allocatedAmount)) < 0
    },
    clearAllocations() {
      this.availableInvoiceList.forEach(invoice => {
        invoice.allocatedAmount = 0
      })
      this.invoiceListUpdated()
    },
    autoAllocation() {
      var amount = this.paymentAmount

      this.availableInvoiceList.forEach(invoice => {
        if(amount > 0) {
          let allocateAmount = Utils.roundCurrency(Math.min(invoice.amountDue, amount))
          invoice.allocatedAmount = allocateAmount
          amount -= allocateAmount
        }
      })
      this.invoiceListUpdated()
    },
    clearInvoiceAllocation(invoice) {
      invoice.allocatedAmount = 0
      this.invoiceListUpdated()
    },
    addInvoiceAllocation(invoice) {
      let availableAmount = this.totalOverpaymentValue
      if(availableAmount > 0) {
        invoice.allocatedAmount = Utils.roundCurrency(Math.min(invoice.amountDue, availableAmount))
        this.invoiceListUpdated()
      }
    },
    addInvoiceToPayment(invoice) {
      var newPaymentAmount = this.currentPayment

      // Make sure we have enough for current allocations
      if(this.currentPayment - this.totalInvoiceAllocation < 0)
        newPaymentAmount = Utils.roundCurrency(this.totalInvoiceAllocation)

      // Add to the payment to pay this invoice
      let requiredAmount = Math.max(invoice.amountDue - this.parseDecimal(invoice.allocatedAmount), 0)
      let availablePayment = Math.max(newPaymentAmount - this.totalInvoiceAllocation, 0)
      let additionalPayment = requiredAmount - availablePayment

      if(additionalPayment > 0)
        newPaymentAmount = Utils.roundCurrency(newPaymentAmount + additionalPayment)
      
      invoice.allocatedAmount = invoice.amountDue
      this.$emit('updatePayment', newPaymentAmount)
      this.invoiceListUpdated()
    },

    invoiceListUpdated() {
      this.$emit('update', this.availableInvoiceList)
    }
  }
}

</script>